/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


import { Flex, Separator, Text } from '@radix-ui/themes';

import { use, useCallback, useState } from 'fierry/react';

import Navigation, { useArg } from 'solis/navigation';
import Routes from 'solis/enum/route';

import Primitive from 'solis/react/primitive/text';

import { getCurrencyReward, getScoreCount } from 'solis/logic/board/cass';

import descriptor, { DURATION_STEPS, getRounds } from 'solis/claude/action/descriptor/create.board';
import ActionCard from 'solis/claude/action/card';
import ActionSlider from 'solis/claude/action/input/slider';

import { formatCompactDuration } from 'solis/claude/util/duration';


const RISK_STEP = 0.05;


const formatRisk = (risk) =>
{
  return '+' + Math.round(risk * 100) + '%';
};


const OnValueChange = ([ value ], setLive) =>
{
  setLive(value);
};


const OnCreated = (dispatch) =>
{
  dispatch.pushForeground({ route: Routes.Dashboard });
};


const Control = ({ label, value, ends, children }) =>
(
  <Flex direction = 'column' gap = '1'>
    <Flex justify = 'between' align = 'baseline'>
      <Primitive.Note>{label}</Primitive.Note>
      {value}
    </Flex>
    {children}
    <Flex justify = 'between'>
      <Text size = '1' color = 'gray'>{ends[0]}</Text>
      <Text size = '1' color = 'gray'>{ends[1]}</Text>
    </Flex>
  </Flex>
);


const Row = ({ label, value, unit }) =>
(
  <Flex justify = 'between' align = 'baseline'>
    <Primitive.Note>{label}</Primitive.Note>
    <Flex align = 'baseline' gap = '1'>
      <Text size = '2' weight = 'bold'>{value}</Text>
      {unit && <Text size = '1' color = 'gray'>{unit}</Text>}
    </Flex>
  </Flex>
);


export default ({ context }) =>
{
  const dispatch = use(Navigation.Dispatch);

  const settings = context.colony.getSettings();

  const [ duration, setDuration ] = useState(useArg('duration', descriptor.scope) ?? descriptor.inputs.duration);
  const [ risk, setRisk ] = useState(useArg('risk', descriptor.scope) ?? descriptor.inputs.risk);

  const onDurationChange = useCallback(OnValueChange, [ setDuration ]);
  const onRiskChange = useCallback(OnValueChange, [ setRisk ]);

  const onCreated = useCallback(OnCreated, [ dispatch ]);

  const rounds = getRounds(settings, duration);

  const length = <Text size = '2' weight = 'bold'>{formatCompactDuration(settings.getDuration(rounds))}</Text>;

  const strength = (
    <Flex align = 'baseline' gap = '1'>
      <Text size = '1' color = 'gray'>attackers up to</Text>
      <Text size = '2' weight = 'bold'>{formatRisk(risk)}</Text>
      <Text size = '1' color = 'gray'>stronger</Text>
    </Flex>
  );

  return (
    <ActionCard label = 'New board' descriptor = {descriptor} context = {context} commit = 'Create' onSuccess = {onCreated}>

      <Control label = 'Duration' value = {length} ends = {[ formatCompactDuration(settings.getDuration(getRounds(settings, 0))), formatCompactDuration(settings.getDuration(getRounds(settings, DURATION_STEPS.length - 1))) ]}>
        <ActionSlider scope = {descriptor.scope} name = 'duration' initial = {descriptor.inputs.duration} min = {0} max = {DURATION_STEPS.length - 1} step = {1} format = {null} onValueChange = {onDurationChange} />
      </Control>

      <Control label = 'Risk' value = {strength} ends = {[ formatRisk(settings.getMinBoardRisk()), formatRisk(settings.getMaxBoardRisk()) ]}>
        <ActionSlider scope = {descriptor.scope} name = 'risk' initial = {descriptor.inputs.risk} min = {settings.getMinBoardRisk()} max = {settings.getMaxBoardRisk()} step = {RISK_STEP} format = {null} onValueChange = {onRiskChange} />
      </Control>

      <Separator size = '4' />

      <Row label = 'Reward' value = {getCurrencyReward(settings, rounds, risk)} unit = 'currency' />
      <Row label = 'Authority' value = {'×' + getScoreCount(settings, rounds)} />

    </ActionCard>
  );
};
